/**
 * liveEntryAdapter — la séance que « GO ! » ouvre, telle que l'avant-séance la montre.
 *
 * QUELLE séance : celle de la route (`liveSessionId`), dans `currentUser.live_sessions` — ce que
 * lisait `PreCallScreen`. Sa ligne passe par l'adaptateur des séances (`toUILiveSessions`), sur sa
 * prochaine occurrence non finalisée (`nextLiveFromToday`), comme la carte du prochain live.
 *
 * QUI est déjà là : les élèves de la séance que la salle voit connectés (`liveMode.liveParticipants`),
 * jamais soi-même, jamais le tuteur. On montre leurs visages et COMBIEN ils sont, jamais qui
 * (Wajih, 17/10) : `UIStackPerson` n'a qu'un id et une image. Le tuteur, la même source le dit
 * présent (`tutorHere`) ; elle ne dit pas son absence : avant d'entrer, la salle n'est pas suivie.
 *
 * Le starting block : le drapeau `checkpoint-sb` de la salle (`live_room.version_ft`).
 */
import moment from "moment"

import { cleanMediaUrl } from "../../../../../data/constants/apiConstants"
import { nextLiveFromToday } from "../../../../../data/utility/liveDay"
import { hasFeature } from "../../../../../data/utility/Utils"
import { type UILiveSession } from "../../../ui/types"
import { type UIStackPerson } from "../../../ui/types/studentLive"
import { type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"
import {
    toUILiveSessions,
    type ApiLiveOccurrence,
    type ApiLiveSessionNested,
    type ApiStudentForSubjects,
} from "../../parent/LiveSessions/liveSessionListAdapter"

type ApiLiveRoom = { name?: string | null; version_ft?: string[] | string | null }
type ApiLiveStudent = { id?: number | string; is_tutor?: boolean; avatar?: string | null; firstname?: string | null }

export type ApiEntryLiveSession = Omit<ApiLiveSessionNested, "students"> & {
    id?: number | string
    live_room?: ApiLiveRoom | null
    students?: (ApiLiveStudent & NonNullable<ApiLiveSessionNested["students"]>[number])[] | null
    next_occurrences?: { date?: string; live_session_id?: number | string; finalized_at?: string | null }[] | null
}

export type ApiEntryAccount = ApiStudentForSubjects & {
    id?: number | string
    live_sessions?: ApiEntryLiveSession[] | null
    is_payment_blocked?: boolean
}

export type LiveEntryModel = {
    /** La séance est dans le compte : le test guidé s'ouvre. */
    found: boolean
    session: UILiveSession | null
    roomName: string
    present: UIStackPerson[]
    presentLabel: string | undefined
    /** Le tuteur de la séance est parmi les connectés que voit la salle. */
    tutorHere: boolean
    startingBlockEnabled: boolean
}

const COUNT_WORDS = ["", "Un", "Deux", "Trois", "Quatre", "Cinq", "Six"]

/** « Un autre élève est déjà là », « Deux autres élèves sont déjà là » : combien, jamais qui. */
export function presentLabelFor(count: number): string | undefined {
    if (count <= 0) return undefined
    const amount = COUNT_WORDS[count] ?? `${count}`
    return count === 1 ? `${amount} autre élève est déjà là` : `${amount} autres élèves sont déjà là`
}

export function findEntryLiveSession(
    account: ApiEntryAccount | null | undefined,
    liveSessionId: number | string
): ApiEntryLiveSession | null {
    return account?.live_sessions?.find((candidate) => String(candidate?.id) === String(liveSessionId)) ?? null
}

/**
 * QUI est déjà là, la règle des deux précalls (élève et masteur) : les élèves de la séance que la salle voit
 * connectés, jamais un tuteur, jamais `excludeId` (l'élève qui regarde). Dans l'ordre de la séance.
 */
export function connectedStudents<Student extends ApiLiveStudent>(
    students: (Student | null | undefined)[] | null | undefined,
    liveParticipants: (number | string)[] | null | undefined,
    excludeId?: number | string | null
): Student[] {
    const connected = new Set((liveParticipants ?? []).map(String))
    return (students ?? []).filter(
        (student): student is Student =>
            student?.id != null &&
            student.is_tutor !== true &&
            (excludeId == null || String(student.id) !== String(excludeId)) &&
            connected.has(String(student.id))
    )
}

/** Un élève déjà là, en visage. */
export function toStackPerson(student: ApiLiveStudent): UIStackPerson {
    return { id: student.id as number | string, uri: student.avatar ? cleanMediaUrl(student.avatar) : undefined }
}

export function toLiveEntry(args: {
    account: ApiEntryAccount | null | undefined
    liveSessionId: number | string
    apiSubjects: ApiSchoolSubject[] | null | undefined
    /** Les identités que la salle voit connectées. */
    liveParticipants: (number | string)[] | null | undefined
    now?: moment.Moment
}): LiveEntryModel {
    const { account, liveSessionId, apiSubjects, liveParticipants, now = moment() } = args
    const liveSession = findEntryLiveSession(account, liveSessionId)
    if (liveSession == null) {
        return {
            found: false,
            session: null,
            roomName: "",
            present: [],
            presentLabel: undefined,
            tutorHere: false,
            startingBlockEnabled: false,
        }
    }

    const occurrence = nextLiveFromToday([liveSession], now) as
        { date?: string; finalized_at?: string | null } | undefined
    const date = occurrence?.date ?? moment(now).format("YYYY-MM-DD HH:mm")
    const row: ApiLiveOccurrence = {
        id: `${liveSessionId}:${date}`,
        date,
        tutor: liveSession.tutor,
        live_session: liveSession,
    }
    const [session] = toUILiveSessions([row], {
        student: account,
        apiSubjects: apiSubjects ?? [],
        paymentBlocked: account?.is_payment_blocked === true,
    })

    const present = connectedStudents(liveSession.students, liveParticipants, account?.id).map(toStackPerson)
    const tutorId = liveSession.tutor?.id

    return {
        found: true,
        session: session ?? null,
        roomName: liveSession.live_room?.name ?? "",
        present,
        presentLabel: presentLabelFor(present.length),
        tutorHere: tutorId != null && (liveParticipants ?? []).some((id) => String(id) === String(tutorId)),
        startingBlockEnabled: hasFeature(liveSession.live_room, "checkpoint-sb") === true,
    }
}
